import React, { useState, useRef } from "react";
import { apiPost, apiGet } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";

export interface OldVisitFormProps {
  onVisitAdded?: () => void;
}

interface FormDataState {
  visit_date: string;
  patient_id: string;
  name: string;
  age: string;
  gender: string;
  area: string;
  reference_source: string;
  medical_alerts: string;
  remarks: string;
  visit_type: string;
}

const initialFormData: FormDataState = {
  visit_date: "",
  patient_id: "",
  name: "",
  age: "",
  gender: "",
  area: "",
  reference_source: "Board/Walk-in",
  medical_alerts: "",
  remarks: "",
  visit_type: "Old Record",
};

const OldVisitForm = ({ onVisitAdded }: OldVisitFormProps) => {
  const { showToast } = useToast();
  const activeSearchRef = useRef(0);
  const [formData, setFormData] = useState<FormDataState>(initialFormData);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExistingPatient, setIsExistingPatient] = useState(false);

  const normalizeDate = (localDate: string | Date): string => {
    if (!localDate) return "";
    const d = new Date(localDate);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split("T")[0];
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === "patient_id") {
      const numeric = value.replace(/\D/g, "");
      if (numeric.length > 15) return;
      setFormData((prev) => ({ ...prev, patient_id: numeric }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const patientIdLen = formData.patient_id.length;
  const patientIdState =
    patientIdLen === 0 ? "idle" : patientIdLen < 10 ? "warn" : patientIdLen === 10 ? "ok" : "error";

  const handleMobileBlur = async () => {
    const mobile = formData.patient_id.trim();
    if (mobile.length !== 10) return;

    const requestId = ++activeSearchRef.current;
    setIsSearching(true);

    try {
      const existing = await apiGet(`/api/patients/${mobile}`);

      if (requestId !== activeSearchRef.current) return;

      const ex = existing as { name?: string; age?: string; gender?: string; area?: string; medical_alerts?: string } | null;
      if (ex) {
        setFormData((prev) => ({
          ...prev,
          name: ex.name || "",
          age: ex.age ?? "",
          gender: ex.gender || "",
          area: ex.area || "",
          medical_alerts: ex.medical_alerts || "",
        }));
        setIsExistingPatient(true);
        showToast({ message: "Found existing patient profile", type: "success" });
      } else {
        setIsExistingPatient(false);
      }
    } catch {
      if (requestId !== activeSearchRef.current) return;
      setIsExistingPatient(false);
    } finally {
      if (requestId === activeSearchRef.current) {
        setIsSearching(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.visit_date || !formData.patient_id || !formData.name) {
      showToast({ message: "Missing Required Fields (Date, Mobile, Name)", type: "warning" });
      return;
    }
    if (formData.patient_id.trim().length !== 10) {
      showToast({ message: "Mobile must be exactly 10 digits", type: "warning" });
      return;
    }

    setIsSaving(true);

    try {
      const safeDate = normalizeDate(formData.visit_date);

      if (!isExistingPatient) {
        try {
          await apiGet(`/api/patients/${formData.patient_id}`);
        } catch {
          await apiPost("/api/patients/", {
            patient_id: formData.patient_id,
            name: formData.name,
            age: formData.age,
            gender: formData.gender,
            area: formData.area,
            location: formData.area,
            reference_source: formData.reference_source || "Board/Walk-in",
            medical_alerts: formData.medical_alerts,
            mobile: formData.patient_id,
          });
        }
      }

      await apiPost("/api/visits/", {
        patient_id: formData.patient_id,
        visit_date: safeDate,
        visit_type: formData.visit_type,
        remarks: formData.remarks,
      });

      showToast({ message: "Old Record Archived Successfully!", type: "success" });
      onVisitAdded?.();
      setFormData({ ...initialFormData });
      setIsExistingPatient(false);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      const backendMsg = e?.response?.data?.error || e?.message || "Unknown error";

      if (backendMsg.includes("already exists")) {
        showToast({ message: "Visit already exists for this date", type: "error" });
      } else {
        showToast({ message: "Save Failed: " + backendMsg, type: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="ui-card rc-form-card">
      <div className="rc-form-header">
        <h3 className="rc-form-title">📜 Old Notebook Entry</h3>
        <span className="rc-badge-warn">History Mode</span>
      </div>

      <form onSubmit={handleSubmit} className="rc-form">
        <div className="rc-form-grid rc-grid-200">
          <div className="rc-field">
            <label className="rc-label">Date of Visit *</label>
            <input
              type="date"
              name="visit_date"
              className={`rc-input ${formData.visit_date ? "" : "rc-input-error"}`}
              required
              max={new Date().toISOString().split("T")[0]}
              value={formData.visit_date}
              onChange={handleChange}
            />
          </div>
          <div className="rc-field rc-field-rel">
            <label className="rc-label">Mobile / ID *</label>
            <input
              name="patient_id"
              className={`rc-input rc-input-has-right rc-input-mobile rc-input-state-${patientIdState}`}
              placeholder="10-digit Mobile"
              required
              value={formData.patient_id}
              onChange={handleChange}
              onBlur={handleMobileBlur}
              inputMode="numeric"
            />
            {isSearching && <div className="rc-input-spinner" />}
          </div>
        </div>

        <div className="rc-form-grid rc-grid-1">
          <div className="rc-field">
            <label className="rc-label">Patient Name *</label>
            <input
              name="name"
              className={`rc-input ${formData.name ? "" : "rc-input-error"}`}
              placeholder="Full Name"
              required
              value={formData.name}
              onChange={handleChange}
              disabled={isExistingPatient}
              title={isExistingPatient ? "Loaded from existing profile" : ""}
            />
          </div>
        </div>

        <div className="rc-form-grid rc-grid-1-1-2-2">
          <div className="rc-field">
            <label className="rc-label">Age</label>
            <input
              name="age"
              type="number"
              className="rc-input"
              value={formData.age}
              onChange={handleChange}
              disabled={isExistingPatient}
            />
          </div>
          <div className="rc-field">
            <label className="rc-label">Gender</label>
            <select
              name="gender"
              className="rc-input"
              value={formData.gender}
              onChange={handleChange}
              disabled={isExistingPatient}
            >
              <option value="">--</option>
              <option value="Male">M</option>
              <option value="Female">F</option>
            </select>
          </div>
          <div className="rc-field">
            <label className="rc-label">Area</label>
            <input
              name="area"
              className="rc-input"
              value={formData.area}
              onChange={handleChange}
              disabled={isExistingPatient}
            />
          </div>
          <div className="rc-field">
            <label className="rc-label">Reference</label>
            <select
              name="reference_source"
              className="rc-input"
              value={formData.reference_source}
              onChange={handleChange}
              disabled={isExistingPatient}
            >
              {["Board/Walk-in", "Old Patient", "Referred by Old Patient", "Doctor's Referral", "Online/Media"].map(
                (ref) => (
                  <option key={ref} value={ref}>
                    {ref}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        <div className="rc-field rc-field-spacer">
          <label className="rc-label rc-label-alert">⚠️ Medical Alerts (Optional)</label>
          <input
            name="medical_alerts"
            className={`rc-input rc-input-alert ${formData.medical_alerts ? "rc-input-alert-strong" : "rc-input-alert-soft"}`}
            placeholder="Allergies, BP, Diabetes..."
            value={formData.medical_alerts}
            onChange={handleChange}
          />
        </div>

        <div className="rc-field">
          <label className="rc-label">Treatment Details / Remarks</label>
          <textarea
            name="remarks"
            className="rc-input rc-textarea"
            rows={2}
            value={formData.remarks}
            onChange={handleChange}
            placeholder="e.g. RCT completed on 26, Paid 500"
          />
        </div>

        <div className="rc-form-footer rc-form-footer-end">
          <Button type="submit" variant="primary" isLoading={isSaving} className="rc-btn-warm">
            {isSaving ? "Archiving..." : "Save Old Record"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OldVisitForm;
